import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import silence from "@/assets/audio/silence.wav"
import { resetAudioPlayback, setSoundEnabled } from "@/store/audioPlaybackSlice"

const GESTURE_EVENTS = ["click", "touchend", "keydown"] as const

let sharedAudio: HTMLAudioElement | null = null
let isAlarmUsingAudio = false

// Safari unlocks sound per element, so every alarm reuses the one a tap unlocked.
const getSharedAudio = () => {
   sharedAudio ??= new Audio()
   return sharedAudio
}

const isBlockedByBrowser = (error: unknown) =>
   error instanceof DOMException && error.name === "NotAllowedError"

// Browsers block sound until the user taps the page, so retry on the next gesture.
export function useAutoPlay(
   src: string,
   loop: boolean,
   enabled: boolean
): void {
   const dispatch = useDispatch()
   const [isBlocked, setIsBlocked] = useState(false)

   useEffect(() => {
      if (!enabled) return

      const audio = getSharedAudio()
      const handleEnded = () => dispatch(resetAudioPlayback())

      isAlarmUsingAudio = true
      audio.src = src
      audio.loop = loop
      audio.addEventListener("ended", handleEnded)
      audio
         .play()
         .then(() => dispatch(setSoundEnabled(true)))
         .catch((error: unknown) => {
            if (!isBlockedByBrowser(error)) return
            setIsBlocked(true)
            dispatch(setSoundEnabled(false))
         })

      return () => {
         isAlarmUsingAudio = false
         audio.removeEventListener("ended", handleEnded)
         audio.pause()
      }
   }, [dispatch, enabled, loop, src])

   useEffect(() => {
      if (!isBlocked) return

      const retryPlayback = () => {
         getSharedAudio()
            .play()
            .then(() => {
               setIsBlocked(false)
               dispatch(setSoundEnabled(true))
            })
            // Still blocked: keep prompting and retry on the next gesture.
            .catch(() => {})
      }

      GESTURE_EVENTS.forEach((eventName) =>
         document.addEventListener(eventName, retryPlayback, true)
      )
      return () => {
         GESTURE_EVENTS.forEach((eventName) =>
            document.removeEventListener(eventName, retryPlayback, true)
         )
      }
   }, [dispatch, isBlocked])
}

// Unlocks the shared element on the first tap, so the first alarm needs no prompt.
export function useUnlockAlarmSound(): void {
   const dispatch = useDispatch()

   useEffect(() => {
      let isUnlocking = false

      const removeListeners = () =>
         GESTURE_EVENTS.forEach((eventName) =>
            document.removeEventListener(eventName, unlock, true)
         )

      function unlock() {
         // A blocked alarm's own retry unlocks it audibly.
         if (isAlarmUsingAudio) return
         // One tap fires touchend and click; play once.
         if (isUnlocking) return

         isUnlocking = true
         const audio = getSharedAudio()
         // Silence, not a muted clip: iPad Safari played the muted clip out loud.
         audio.src = silence
         audio
            .play()
            .then(() => {
               removeListeners()
               dispatch(setSoundEnabled(true))
               if (!isAlarmUsingAudio) audio.pause()
            })
            .catch(() => {})
            .finally(() => {
               isUnlocking = false
            })
      }

      GESTURE_EVENTS.forEach((eventName) =>
         document.addEventListener(eventName, unlock, true)
      )
      return removeListeners
   }, [dispatch])
}
